import moment from 'moment/moment';
import { createFilterOptions } from '@mui/material';

export const PRICE_PATTERN = /^(^([$]?)([0-9,]+(\.[0-9]+)?)([KkMmBb+])?)?$/g;

// Land size: numbers, optional decimal, required area unit
export const LAND_SIZE_PATTERN = /^(^([$]?)([0-9,]+(\.[0-9]+)?)(M2|m2|m²|ha|HA|Ha|hA|\+)?)?$/g;

const splitUnitAndValue = (str, pattern) => {
    const splitValue = {
        unit: '',
        numericValue: '',
    };
    const regex = new RegExp(pattern);
    splitValue.unit = str.replace(regex, '$5');
    splitValue.numericValue = str.replace(regex, '$3');
    return splitValue;
};

const getFormattedUnitAndValue = (value, unitPattern) => {
    const {numericValue, unit} = splitUnitAndValue(value, unitPattern);
    return {
        unit: unit.toLowerCase(),
        value: removeCommaAndSpace(numericValue),
    };
};

const numberWithCommas = (number, fractionDigits = 0) => {
    if (number === '-' || number === null || number === undefined) return number || '';
    const num = Number(number);
    if (!Number.isFinite(num)) return '';
    return num.toLocaleString(undefined, {minimumFractionDigits: fractionDigits});
};

const abbreviateNumber = (input) => {
    const number = parseInt(input, 10);
    if (isNaN(number)) return '';
    if (number < 1000) return number.toString();

    const units = ['', 'K', 'M', 'B'];
    const tier = Math.floor(Math.log10(Math.abs(number)) / 3);
    const postfix = units[tier] || '';
    const scale = 10 ** (tier * 3);
    const scaled = number / scale;

    // Use 5000 as modulo for millions and above, otherwise 10
    const modulo = number >= 1_000_000 ? 5000 : 10;
    if (number % modulo === 0) {
        return `${Math.round(scaled * 100) / 100}${postfix}`;
    }
    return numberWithCommas(number);
};

const expandAbbreviatedValue = (value, isMeter = false, unitPattern, unitMultipliers) => {
    if (typeof value !== 'string' || !value) return value;
    const {unit, value: _value} = getFormattedUnitAndValue(value, unitPattern);
    if (!unit || Number.isNaN(_value)) return _value;
    if (unit === 'm' && !isMeter) return (_value * unitMultipliers.m).toString();
    const multiplier = unitMultipliers[unit] ?? 1;
    return (_value * multiplier).toString();
};

const expandPrice = (value) => expandAbbreviatedValue(
    value,
    false,
    PRICE_PATTERN,
    { k: 1_000, m: 1_000_000, b: 1_000_000_000, '+': 1 },
);

const expandAreaValue = (value, isMeter) => expandAbbreviatedValue(
    value,
    isMeter,
    LAND_SIZE_PATTERN,
    { ha: 10_000, 'm²': 1 }
);


const abbreviatePrice = (value) => {
    if (value) {
        const {numericValue, unit} = splitUnitAndValue(value, PRICE_PATTERN);
        if (numericValue && !Number.isNaN(parseFloat(numericValue))) {
            if (unit !== '') {
                return `${numericValue}${unit.toUpperCase()}`;
            }
            return `$${abbreviateNumber(removeCommaAndSpace(numericValue))}`;
        }
    }
    return null;
};

// This would expand the value if it's abbreviated (e.g., '1.5K' to '1500')
// and convert it to a standard price format if it's a currency.
const expandAndFormatPrice = (value) => {
    if (!value) return value;
    const expanded = expandPrice(value);
    return expanded ? `$${numberWithCommas(expanded)}` : '';
};

const removeCommaAndSpace = str => str.replace(/[, ]+/g, '').trim();

const getNumericValue = (value) => value.replace(/[^0-9.]/g, '');

const expandAndFormatArea = (value, isMeter) => {
    if (!value) return value;
    const expanded = expandAreaValue(value, isMeter);
    return expanded ? `${numberWithCommas(expanded)} m²` : '';
};

const formatDistance = (value) => {
    if (value.toString().toLowerCase() === 'min' || value.toString().toLowerCase() === 'max') return value;
    return `${value}Km`;
};

const formatStreetFrontage = (value) => {
    if (value.toString().toLowerCase() === 'min' || value.toString().toLowerCase() === 'max') return value;
    return `${value}m`;
};

const formatRange = (min, max, formatMin) => {
    if (min && !max) return formatMin(min);
    if (!min && max) return `0 - ${max}`;
    if (min && max) {
        if (min === max) return max;
        return `${min} - ${max}`;
    }
    return '';
};

const formatSixPlusRange = (min, max) =>
    formatRange(min, max, m => m.endsWith('+') ? m : `${m}+`);

const formatStandardRange = (min, max) =>
    formatRange(min, max, m => `${m}+`);

const formatBedBathCarRangeDisplay = (min, max) => formatSixPlusRange(min, max);

const formatPriceRangeDisplay = (min, max) => {
    const _min = abbreviatePrice(min);
    const _max = abbreviatePrice(max);
    return formatStandardRange(_min, _max);
};

export const SALE_DATE_FORMAT = 'DD MMM YYYY';
export const getDateRangeFromMonths = (months) => {
    const endDate = moment();
    const startDate = moment().subtract(months, 'months');
    return `${startDate.format(SALE_DATE_FORMAT)} - ${endDate.format(SALE_DATE_FORMAT)}`;
};

const filter = createFilterOptions();
const filterOptions = (options, params) => {
    const filtered = filter(options, params);

    const { inputValue } = params;
    // Suggest the creation of a new value
    const isExisting = options.some((option) => inputValue.toLowerCase() === option.label.toLowerCase());
    if (inputValue !== '' && !isExisting) {
        filtered.push({
            value: inputValue,
            label: inputValue,
        });
    }

    return filtered;
};

const isOptionEqualToValue = (option, value) => String(option?.value) === String(value?.value);

const getOptionDisabled = (otherValue, compareFn) => (option) => {
    if (!option ||
        !otherValue ||
        option.value === '' ||
        otherValue.value === '') {
        return false;
    }

    return compareFn(Number(expandPrice(option.value)), Number(otherValue.value));
};

const getMinOptionDisabled = (maxValue) =>
    getOptionDisabled(maxValue, (a, b) => a > b);

const getMaxOptionDisabled = (minValue) =>
    getOptionDisabled(minValue, (a, b) => a < b);

const getDisplayValue = (options = [], value, formatter, error) => {
    if (!value) return value;
    if (formatter) {
        const valueStr = value.value?.toString().toLowerCase?.();
        let selected = options.find(
            option => option.value?.toString().toLowerCase?.() === valueStr
        );
        if (!selected) {
            selected = {
                label: error ? value.label : formatter(value.label),
                value: value.value,
            };
        }
        return selected;
    }
    return value;
};
const getDisplayPriceValue = (options = [], value, formatter, error) => {
    if (!value) return value;
    if (formatter) {
        const valueStr = value.value?.toString().toLowerCase?.();
        let selected = options.find(
            option => option.value?.toString().toLowerCase?.() === valueStr
        );
        if (!selected) {
            if (error?.endsWith('digits')) {
                selected = {
                    label: formatter(value.label),
                    value: value.value,
                };
            } else {
                selected = {
                    label: error ? value.label : formatter(value.label),
                    value: value.value,
                };
            }
        }
        return selected;
    }
    return value;
};

const findOptionByValue = (options, value) => {
    return options.find(opt => opt.value === value) || { value: value, label: value };
};

const handleAutocompleteInputChange = (e, { params, maxInputLength }) => {
    const t = e.target;
    // limits input length
    if (maxInputLength && t.value.length > maxInputLength) t.value = t.value.slice(0, maxInputLength);
    params?.inputProps?.onInput?.(e);
};

const SearchFilterUtil = {
    formatBedBathCarRangeDisplay,
    formatPriceRangeDisplay,
    formatDistance,
    expandAndFormatPrice,
    expandPrice,
    filterOptions,
    isOptionEqualToValue,
    getMinOptionDisabled,
    getMaxOptionDisabled,
    getDisplayValue,
    expandAndFormatArea,
    findOptionByValue,
    numberWithCommas,
    handleAutocompleteInputChange,
    getNumericValue,
    formatStreetFrontage,
    getDisplayPriceValue,
};

export default SearchFilterUtil;
